'use client'

import { useState, useEffect } from 'react'
import { Button } from '@planner/ui'
import { useAuth } from '@/contexts/AuthContext'
import { supabase } from '@/lib/supabase'
import { Mail, X } from 'lucide-react'

export function EmailConfirmationBanner() {
  const { user, session } = useAuth()
  const [isEmailConfirmed, setIsEmailConfirmed] = useState<boolean | null>(null)
  const [isDismissed, setIsDismissed] = useState(false)
  const [isResending, setIsResending] = useState(false)

  useEffect(() => {
    // Check if email is confirmed
    if (user && session) {
      const confirmed = !!user.email_confirmed_at
      setIsEmailConfirmed(confirmed)
      
      // If email is confirmed, dismiss the banner
      if (confirmed) {
        setIsDismissed(true)
      }
    } else {
      setIsEmailConfirmed(null)
    }
  }, [user, session])

  const handleResendEmail = async () => {
    if (!user?.email) return

    setIsResending(true)
    try {
      const response = await fetch('/api/auth/send-confirmation-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: user.email,
          userId: user.id,
          fullName: user.user_metadata?.full_name,
        }),
      })

      const data = await response.json()

      if (!response.ok && !data.fallback) {
        console.error('Failed to resend confirmation email:', data.error)
        alert('Nie udało się wysłać emaila. Spróbuj ponownie później.')
      } else {
        alert('Email potwierdzający został wysłany. Sprawdź swoją skrzynkę pocztową.')
      }
    } catch (error) {
      console.error('Error resending confirmation email:', error)
      alert('Wystąpił błąd podczas wysyłania emaila. Spróbuj ponownie później.')
    } finally {
      setIsResending(false)
    }
  }

  // Don't show if email is confirmed, dismissed, or user is not logged in
  if (isEmailConfirmed !== false || isDismissed || !user) {
    return null
  }

  return (
    <div className="w-full bg-amber-50 dark:bg-amber-950/20 border-b border-amber-200 dark:border-amber-800 text-amber-900 dark:text-amber-100 px-4 py-3 text-sm flex items-center justify-between gap-3">
      <div className="flex items-center gap-2 flex-1">
        <Mail className="h-4 w-4 flex-shrink-0" />
        <div className="flex-1">
          <span className="font-medium">Email nie został potwierdzony</span>
          <span className="ml-2 text-xs text-amber-700 dark:text-amber-300">
            Sprawdź swoją skrzynkę pocztową i kliknij w link potwierdzający.
          </span>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={handleResendEmail}
          disabled={isResending}
          className="text-xs"
        >
          {isResending ? 'Wysyłanie...' : 'Wyślij ponownie'}
        </Button>
        <button
          onClick={() => setIsDismissed(true)}
          className="p-1 hover:bg-amber-100 dark:hover:bg-amber-900/30 rounded transition-colors"
          aria-label="Zamknij"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}
